import React, { useState, useEffect, useRef } from 'react';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Chart } from 'primereact/chart';
import { Badge } from 'primereact/badge';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { Panel } from 'primereact/panel';
import { Divider } from 'primereact/divider';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { SelectButton } from 'primereact/selectbutton';
import { useAuth } from '../contexts/AuthContext';
import {
  ClipboardDocumentListIcon,
  BanknotesIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  ChartPieIcon,
  CheckCircleIcon,
  PlusIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';

// import { 
//   attendanceAPI, 
//   feesAPI, 
//   studentsAPI, 
//   employeesAPI, 
//   classesAPI,
//   examsAPI 
// } from '../services/api';
import { 
  ChartBarIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  CalendarIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { classesAPI } from '../services/classService';

const Reports = () => {
  const { user } = useAuth();
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), new Date()]);
  const [classes, setClasses] = useState([]);
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [reportType, setReportType] = useState('summary');
  
  // Report data states
  const [attendanceReport, setAttendanceReport] = useState(null);
  const [feesReport, setFeesReport] = useState(null);
  const [studentsReport, setStudentsReport] = useState(null);
  const [employeesReport, setEmployeesReport] = useState(null);
  const [academicReport, setAcademicReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  
  // Dialog states
  const [previewDialog, setPreviewDialog] = useState(false);
  const [filterDialog, setFilterDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    includeAttendance: true,
    includeFees: true,
    includeExams: true,
    includeStudents: true,
    includeEmployees: false,
    format: 'pdf'
  });

  const reportTypes = [
    { label: 'Summary Report', value: 'summary' },
    { label: 'Detailed Report', value: 'detailed' },
    { label: 'Comparative Report', value: 'comparative' }
  ];

  const formatOptions = [
    { label: 'PDF', value: 'pdf' },
    { label: 'Excel', value: 'excel' },
    { label: 'CSV', value: 'csv' }
  ];

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (dateRange && dateRange[0] && dateRange[1]) {
      loadReports();
    }
  }, [dateRange, selectedClasses]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const classesRes = await classesAPI.getAll(user.schoolId);
      setClasses(classesRes.data.data || []);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load initial data'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAttendanceReport(),
        loadFeesReport(),
        loadStudentsReport(),
        loadEmployeesReport(),
        loadAcademicReport(),
        loadFinancialReport()
      ]);
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendanceReport = async () => {
    try {
      // TODO: Implement API call for attendance report
      setAttendanceReport(null);
    } catch (error) {
      console.error('Error loading attendance report:', error);
      setAttendanceReport(null);
    }
  };

  const loadFeesReport = async () => {
    try {
      // TODO: Implement API call for fees report
      setFeesReport(null);
    } catch (error) {
      console.error('Error loading fees report:', error);
      setFeesReport(null);
    }
  };

  const loadStudentsReport = async () => {
    try {
      // TODO: Implement API call for students report
      setStudentsReport(null);
    } catch (error) {
      console.error('Error loading students report:', error);
      setStudentsReport(null);
    }
  };

  const loadEmployeesReport = async () => {
    try {
      // TODO: Implement API call for employees report
      setEmployeesReport(null);
    } catch (error) {
      console.error('Error loading employees report:', error);
      setEmployeesReport(null);
    }
  };

  const loadAcademicReport = async () => {
    try {
      // TODO: Implement API call for academic report
      setAcademicReport(null);
    } catch (error) {
      console.error('Error loading academic report:', error);
      setAcademicReport(null);
    }
  };

  const loadFinancialReport = async () => {
    try {
      // TODO: Implement API call for financial report
      setFinancialReport(null);
    } catch (error) {
      console.error('Error loading financial report:', error);
      setFinancialReport(null);
    }
  };

  const generateReport = async (reportName) => {
    try {
      setLoading(true);
      
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `${reportName} generated successfully`
      });
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: `Failed to generate ${reportName}`
      });
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (format, reportName) => {
    toast.current?.show({
      severity: 'info',
      summary: 'Export',
      detail: `Exporting ${reportName} as ${format.toUpperCase()}...`
    });
  };

  const previewReport = (reportData, reportName) => {
    setSelectedReport({ data: reportData, name: reportName });
    setPreviewDialog(true);
  };

  const getAttendanceChartData = () => {
    if (!attendanceReport) return null;
    
    return {
      labels: attendanceReport.weeklyTrend.map(day => day.day),
      datasets: [
        {
          label: 'Attendance %',
          data: attendanceReport.weeklyTrend.map(day => day.attendance),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4,
          fill: true
        }
      ]
    };
  };

  const getFeesChartData = () => {
    if (!feesReport) return null;
    
    return {
      labels: ['Collected', 'Pending', 'Overdue'],
      datasets: [
        {
          data: [feesReport.totalCollected, feesReport.totalPending, feesReport.totalOverdue],
          backgroundColor: ['#10B981', '#F59E0B', '#EF4444'],
          borderWidth: 2
        }
      ]
    };
  };

  const getStudentsChartData = () => {
    if (!studentsReport) return null;
    
    return {
      labels: ['Male', 'Female'],
      datasets: [
        {
          data: [studentsReport.genderDistribution.male, studentsReport.genderDistribution.female],
          backgroundColor: ['#3B82F6', '#EC4899'],
          borderWidth: 2
        }
      ]
    };
  };

  const getAcademicChartData = () => {
    if (!academicReport) return null;
    
    return {
      labels: academicReport.gradeDistribution.map(grade => grade.grade),
      datasets: [
        {
          label: 'Number of Students',
          data: academicReport.gradeDistribution.map(grade => grade.count),
          backgroundColor: [
            '#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', 
            '#EF4444', '#6B7280', '#F97316', '#84CC16'
          ],
          borderWidth: 2
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
      }
    }
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100
      }
    }
  };

  const leftToolbarTemplate = () => {
    return (
      <div className="flex space-x-2">
        <Button
          label="Generate Report"
          icon="pi pi-file"
          className="p-button-success"
          onClick={() => generateReport('Comprehensive Report')}
          loading={loading}
        />
        <Button
          label="Filters"
          icon="pi pi-filter"
          className="p-button-info"
          onClick={() => setFilterDialog(true)}
        />
      </div>
    );
  };

  const rightToolbarTemplate = () => {
    return (
      <div className="flex space-x-2 items-center">
        <Calendar
          value={dateRange}
          onChange={(e) => setDateRange(e.value)}
          selectionMode="range"
          readOnlyInput
          showIcon
          placeholder="Select Date Range"
        />
        <Dropdown
          value={reportType}
          onChange={(e) => setReportType(e.value)}
          options={reportTypes}
          placeholder="Report Type"
          className="w-48"
        />
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <Toast ref={toast} />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600 mt-1">Comprehensive school performance reports</p>
        </div>
      </div>

      {/* Toolbar */}
      <Card>
        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />
      </Card>

      <TabView>
        <TabPanel header="Attendance Reports" leftIcon="pi pi-calendar">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Total Students</p>
                    <p className="text-3xl font-bold">{attendanceReport?.totalStudents || 0}</p>
                  </div>
                  <UserGroupIcon className="h-12 w-12 text-blue-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Avg Attendance</p>
                    <p className="text-3xl font-bold">{attendanceReport?.averageAttendance || 0}%</p>
                  </div>
                  <ChartBarIcon className="h-12 w-12 text-green-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100">Present Today</p>
                    <p className="text-3xl font-bold">{attendanceReport?.presentToday || 0}</p>
                  </div>
                  <CalendarIcon className="h-12 w-12 text-purple-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100">Absent Today</p>
                    <p className="text-3xl font-bold">{attendanceReport?.absentToday || 0}</p>
                  </div>
                  <ClipboardDocumentListIcon className="h-12 w-12 text-red-200" />
                </div>
              </Card>
            </div>

            {/* Charts and Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Weekly Attendance Trend</h3>
                  <div className="flex space-x-2">
                    <Button
                      icon="pi pi-eye"
                      className="p-button-rounded p-button-text"
                      onClick={() => previewReport(attendanceReport, 'Attendance Report')}
                      tooltip="Preview"
                    />
                    <Button
                      icon="pi pi-download"
                      className="p-button-rounded p-button-text"
                      onClick={() => exportReport('pdf', 'Attendance Report')}
                      tooltip="Export PDF"
                    />
                  </div>
                </div>
                {attendanceReport && (
                  <Chart type="line" data={getAttendanceChartData()} options={lineChartOptions} />
                )}
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-4">Class-wise Attendance</h3>
                <DataTable value={attendanceReport?.classWiseData || []} responsiveLayout="scroll">
                  <Column field="className" header="Class" />
                  <Column field="totalStudents" header="Students" />
                  <Column 
                    field="avgAttendance" 
                    header="Avg Attendance"
                    body={(rowData) => (
                      <div className="flex items-center space-x-2">
                        <ProgressBar value={rowData.avgAttendance} className="w-20" />
                        <span>{rowData.avgAttendance}%</span>
                      </div>
                    )}
                  />
                </DataTable>
              </Card>
            </div>

            {/* Defaulters */}
            <Card>
              <h3 className="text-lg font-semibold mb-4">Attendance Defaulters</h3>
              <DataTable value={attendanceReport?.defaulters || []} responsiveLayout="scroll">
                <Column field="studentName" header="Student Name" />
                <Column field="className" header="Class" />
                <Column 
                  field="attendanceRate" 
                  header="Attendance Rate"
                  body={(rowData) => (
                    <Badge 
                      value={`${rowData.attendanceRate}%`} 
                      severity={rowData.attendanceRate < 70 ? 'danger' : 'warning'}
                    />
                  )}
                />
              </DataTable>
            </Card>
          </div>
        </TabPanel>

        <TabPanel header="Financial Reports" >
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Total Collected</p>
                    <p className="text-3xl font-bold">₹{feesReport?.totalCollected?.toLocaleString() || 0}</p>
                  </div>
                  <BanknotesIcon className="h-12 w-12 text-green-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-100">Pending</p>
                    <p className="text-3xl font-bold">₹{feesReport?.totalPending?.toLocaleString() || 0}</p>
                  </div>
                  <ClockIcon className="h-12 w-12 text-yellow-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-red-500 to-red-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100">Overdue</p>
                    <p className="text-3xl font-bold">₹{feesReport?.totalOverdue?.toLocaleString() || 0}</p>
                  </div>
                  <ExclamationTriangleIcon className="h-12 w-12 text-red-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Collection Rate</p>
                    <p className="text-3xl font-bold">{feesReport?.collectionRate || 0}%</p>
                  </div>
                  <ChartPieIcon className="h-12 w-12 text-blue-200" />
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Fee Collection Status</h3>
                  <div className="flex space-x-2">
                    <Button
                      icon="pi pi-eye"
                      className="p-button-rounded p-button-text"
                      onClick={() => previewReport(feesReport, 'Financial Report')}
                      tooltip="Preview"
                    />
                    <Button
                      icon="pi pi-download"
                      className="p-button-rounded p-button-text"
                      onClick={() => exportReport('excel', 'Financial Report')}
                      tooltip="Export Excel"
                    />
                  </div>
                </div>
                {feesReport && (
                  <Chart type="doughnut" data={getFeesChartData()} options={chartOptions} />
                )}
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-4">Class-wise Fee Collection</h3>
                <DataTable value={feesReport?.classWiseFees || []} responsiveLayout="scroll">
                  <Column field="className" header="Class" />
                  <Column 
                    field="collected" 
                    header="Collected"
                    body={(rowData) => `₹${rowData.collected?.toLocaleString()}`}
                  />
                  <Column 
                    field="pending" 
                    header="Pending"
                    body={(rowData) => `₹${rowData.pending?.toLocaleString()}`}
                  />
                  <Column 
                    header="Collection %"
                    body={(rowData) => {
                      const percentage = ((rowData.collected / rowData.totalFees) * 100).toFixed(1);
                      return (
                        <div className="flex items-center space-x-2">
                          <ProgressBar value={parseFloat(percentage)} className="w-20" />
                          <span>{percentage}%</span>
                        </div>
                      );
                    }}
                  />
                </DataTable>
              </Card>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Academic Reports" leftIcon="pi pi-book">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-indigo-100">Total Exams</p>
                    <p className="text-3xl font-bold">{academicReport?.totalExams || 0}</p>
                  </div>
                  <AcademicCapIcon className="h-12 w-12 text-indigo-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">Average Score</p>
                    <p className="text-3xl font-bold">{academicReport?.averageScore || 0}%</p>
                  </div>
                  <ChartBarIcon className="h-12 w-12 text-green-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100">Pass Rate</p>
                    <p className="text-3xl font-bold">{academicReport?.passRate || 0}%</p>
                  </div>
                  <CheckCircleIcon className="h-12 w-12 text-purple-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100">Upcoming Exams</p>
                    <p className="text-3xl font-bold">{academicReport?.upcomingExams || 0}</p>
                  </div>
                  <CalendarIcon className="h-12 w-12 text-orange-200" />
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Grade Distribution</h3>
                  <div className="flex space-x-2">
                    <Button
                      icon="pi pi-eye"
                      className="p-button-rounded p-button-text"
                      onClick={() => previewReport(academicReport, 'Academic Report')}
                      tooltip="Preview"
                    />
                    <Button
                      icon="pi pi-download"
                      className="p-button-rounded p-button-text"
                      onClick={() => exportReport('pdf', 'Academic Report')}
                      tooltip="Export PDF"
                    />
                  </div>
                </div>
                {academicReport && (
                  <Chart type="bar" data={getAcademicChartData()} options={chartOptions} />
                )}
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-4">Subject Performance</h3>
                <DataTable value={academicReport?.subjectPerformance || []} responsiveLayout="scroll">
                  <Column field="subject" header="Subject" />
                  <Column 
                    field="avgScore" 
                    header="Avg Score"
                    body={(rowData) => `${rowData.avgScore}%`}
                  />
                  <Column 
                    field="passRate" 
                    header="Pass Rate"
                    body={(rowData) => (
                      <div className="flex items-center space-x-2">
                        <ProgressBar value={rowData.passRate} className="w-20" />
                        <span>{rowData.passRate}%</span>
                      </div>
                    )}
                  />
                </DataTable>
              </Card>
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Student Reports" leftIcon="pi pi-users">
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100">Total Students</p>
                    <p className="text-3xl font-bold">{studentsReport?.totalStudents || 0}</p>
                  </div>
                  <UserGroupIcon className="h-12 w-12 text-blue-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100">New Admissions</p>
                    <p className="text-3xl font-bold">{studentsReport?.newAdmissions || 0}</p>
                  </div>
                  <PlusIcon className="h-12 w-12 text-green-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100">Graduations</p>
                    <p className="text-3xl font-bold">{studentsReport?.graduations || 0}</p>
                  </div>
                  <AcademicCapIcon className="h-12 w-12 text-purple-200" />
                </div>
              </Card>

              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-100">Transfers</p>
                    <p className="text-3xl font-bold">{studentsReport?.transfers || 0}</p>
                  </div>
                  <ShareIcon className="h-12 w-12 text-orange-200" />
                </div>
              </Card>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Gender Distribution</h3>
                  <div className="flex space-x-2">
                    <Button
                      icon="pi pi-eye"
                      className="p-button-rounded p-button-text"
                      onClick={() => previewReport(studentsReport, 'Student Report')}
                      tooltip="Preview"
                    />
                    <Button
                      icon="pi pi-download"
                      className="p-button-rounded p-button-text"
                      onClick={() => exportReport('csv', 'Student Report')}
                      tooltip="Export CSV"
                    />
                  </div>
                </div>
                {studentsReport && (
                  <Chart type="doughnut" data={getStudentsChartData()} options={chartOptions} />
                )}
              </Card>

              <Card>
                <h3 className="text-lg font-semibold mb-4">Class Distribution</h3>
                <DataTable value={studentsReport?.classDistribution || []} responsiveLayout="scroll">
                  <Column field="className" header="Class" />
                  <Column field="students" header="Students" />
                  <Column field="capacity" header="Capacity" />
                  <Column 
                    header="Occupancy"
                    body={(rowData) => {
                      const percentage = ((rowData.students / rowData.capacity) * 100).toFixed(1);
                      return (
                        <div className="flex items-center space-x-2">
                          <ProgressBar value={parseFloat(percentage)} className="w-20" />
                          <span>{percentage}%</span>
                        </div>
                      );
                    }}
                  />
                </DataTable>
              </Card>
            </div>
          </div>
        </TabPanel>
      </TabView>

      {/* Preview Dialog */}
      <Dialog
        header={`Preview: ${selectedReport?.name}`}
        visible={previewDialog}
        onHide={() => setPreviewDialog(false)}
        style={{ width: '90vw', maxWidth: '1200px' }}
        footer={
          <div>
            <Button
              label="Close"
              icon="pi pi-times"
              onClick={() => setPreviewDialog(false)}
              className="p-button-text"
            />
            <Button
              label="Print"
              icon="pi pi-print"
              onClick={() => window.print()}
              className="p-button-secondary"
            />
            <Button
              label="Export PDF"
              icon="pi pi-download"
              onClick={() => exportReport('pdf', selectedReport?.name)}
              className="p-button-success"
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div className="text-center border-b pb-4">
            <h2 className="text-2xl font-bold">School Management System</h2>
            <h3 className="text-lg text-gray-600">{selectedReport?.name}</h3>
            <p className="text-sm text-gray-500">
              Generated on {new Date().toLocaleDateString()} | 
              Period: {dateRange[0]?.toLocaleDateString()} - {dateRange[1]?.toLocaleDateString()}
            </p>
          </div>
          
          {selectedReport && (
            <div className="space-y-4">
              <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
                {JSON.stringify(selectedReport.data, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </Dialog>

      {/* Filter Dialog */}
      <Dialog
        header="Report Filters"
        visible={filterDialog}
        onHide={() => setFilterDialog(false)}
        style={{ width: '500px' }}
        footer={
          <div>
            <Button
              label="Reset"
              icon="pi pi-refresh"
              onClick={() => setFilters({
                includeAttendance: true,
                includeFees: true,
                includeExams: true,
                includeStudents: true,
                includeEmployees: false,
                format: 'pdf'
              })}
              className="p-button-text"
            />
            <Button
              label="Apply Filters"
              icon="pi pi-check"
              onClick={() => {
                setFilterDialog(false);
                loadReports();
              }}
              className="p-button-success"
            />
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-3">Include in Report:</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <Checkbox
                  checked={filters.includeAttendance}
                  onChange={(e) => setFilters({ ...filters, includeAttendance: e.checked })}
                  className="mr-2"
                />
                <label>Attendance Data</label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filters.includeFees}
                  onChange={(e) => setFilters({ ...filters, includeFees: e.checked })}
                  className="mr-2"
                />
                <label>Financial Data</label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filters.includeExams}
                  onChange={(e) => setFilters({ ...filters, includeExams: e.checked })}
                  className="mr-2"
                />
                <label>Academic Data</label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filters.includeStudents}
                  onChange={(e) => setFilters({ ...filters, includeStudents: e.checked })}
                  className="mr-2"
                />
                <label>Student Data</label>
              </div>
              <div className="flex items-center">
                <Checkbox
                  checked={filters.includeEmployees}
                  onChange={(e) => setFilters({ ...filters, includeEmployees: e.checked })}
                  className="mr-2"
                />
                <label>Employee Data</label>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold mb-3">Export Format:</h4>
            <SelectButton
              value={filters.format}
              onChange={(e) => setFilters({ ...filters, format: e.value })}
              options={formatOptions}
              className="w-full"
            />
          </div>
          
          <div>
            <h4 className="font-semibold mb-3">Classes:</h4>
            <MultiSelect
              value={selectedClasses}
              onChange={(e) => setSelectedClasses(e.value)}
              options={classes}
              optionLabel="name"
              placeholder="Select Classes"
              className="w-full"
              display="chip"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Reports;